// App.js
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./context/AuthContext";
import { GnosisProvider } from "./context/GnosisContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import rolePermissions from "./config/rolePermissions";

// Pages
import LoginPage from "./pages/LoginPage";
import Unauthorized from "./pages/Unauthorized";
import HomePage from "./pages/HomePage";
import ReportsPage from "./pages/Reports";
import RebateCalculator from "./pages/RebateCalculator";
import Settings from "./pages/Settings";
import AgentAssist from "./pages/AgentAssist";
import Tutorial from "./pages/Tutorial";
import UserManagement from "./pages/UserManagement";
import Gnosis from "./pages/Gnosis";
import HRPay from "./pages/HRPay";
import RebateViewer from "./pages/RebateViewer";
import AudioTranscriber from "./pages/AudioTranscriber";
import AgentAssistGpt4o from "./pages/AgentAssistGpt4o";
import IDPMonitoringDashboard from "./pages/IDPMonitoringDashboard";
import PasswordManager from "./pages/PasswordManager";
import CIKMonitoringDashboard from "./pages/CIKMonitoringDashbord";
import QAAudioTranscriber from "./pages/QAAudioTranscriber";
import ResumeAnalysis from "./pages/ResumeAnalysis";
import CoachingTranscriber from "./pages/CoachingTranscriber";
import WebViewPage from "./pages/WebViewPage";
import AdminAnnouncementsPanel from "./pages/AdminAnnouncementsPanel";
import ToolsOverview from "./pages/ToolsOverview";
import GlobalChatPage from "./pages/GlobalChatPage";
import AudioSentiment from "./pages/AudioSentiment";
import ESATSurvey from "./pages/ESATSurvey";
import LearningPage from "./pages/LearningPage";

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    return () => console.log(`🧱 Leaving route: ${location.pathname}`);
  }, [location.pathname]);
  return null;
};

const DebugWrapper = ({ children, componentName }) => {
  const location = useLocation();
  useEffect(() => {
    return () => console.log(`🔄 ${componentName} unmounting from route: ${location.pathname}`);
  }, [componentName, location.pathname]);
  return <>{children}</>;
};

// Helper: render protected route, optionally wrapped with <Layout />
const renderProtectedRoute = (path, element, componentName, useLayout = true) => (
  <Route element={<ProtectedRoute allowedRoles={rolePermissions[path]} />} key={path}>
    <Route
      path={path}
      element={
        useLayout ? (
          <Layout>
            <DebugWrapper componentName={componentName}>{element}</DebugWrapper>
          </Layout>
        ) : (
          <DebugWrapper componentName={componentName}>{element}</DebugWrapper>
        )
      }
    />
  </Route>
);

const AppRoutes = () => (
  <>
    <RouteTracker />
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      {/* Keep Unauthorized without Layout or add Layout if you want the shell there */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Redirects/Aliases */}
      <Route path="/access-management" element={<Navigate to="/user-management" replace />} />

      {/* Protected (all with Layout by default) */}
      {renderProtectedRoute("/home", <HomePage />, "HomePage")}
      {renderProtectedRoute("/rebate-calculator", <RebateCalculator />, "RebateCalculator")}
      {renderProtectedRoute("/settings", <Settings />, "Settings")}
      {renderProtectedRoute("/agent-assist", <AgentAssist />, "AgentAssist")}
      {renderProtectedRoute("/tutorial", <Tutorial />, "Tutorial")}
      {renderProtectedRoute("/user-management", <UserManagement />, "UserManagement")}
      {renderProtectedRoute("/gnosis", <Gnosis />, "Gnosis")}
      {/* HRPay WITHOUT Layout */}
      {renderProtectedRoute("/hrpay", <HRPay />, "HRPay", false)}
      {renderProtectedRoute("/rebate-viewer", <RebateViewer />, "RebateViewer")}
      {renderProtectedRoute("/audio-transcriber", <AudioTranscriber />, "AudioTranscriber")}
      {renderProtectedRoute("/agent-assist-gpt4o", <AgentAssistGpt4o />, "AgentAssistGpt4o")}
      {renderProtectedRoute("/idp-monitoring", <IDPMonitoringDashboard />, "IDPMonitoring")}
      {renderProtectedRoute("/password-manager", <PasswordManager />, "PasswordManager")}
      {renderProtectedRoute("/qa-audio-transcriber", <QAAudioTranscriber />, "QAAudioTranscriber")}
      {renderProtectedRoute("/resume-analysis", <ResumeAnalysis />, "ResumeAnalysis")}
      {renderProtectedRoute("/coaching-transcriber", <CoachingTranscriber />, "CoachingTranscriber")}
      {renderProtectedRoute("/webview", <WebViewPage />, "WebViewPage")}
      {renderProtectedRoute("/admin/announcements", <AdminAnnouncementsPanel />, "AdminAnnouncementsPanel")}
      {renderProtectedRoute("/tools", <ToolsOverview />, "ToolsOverview")}
      {renderProtectedRoute("/chat", <GlobalChatPage />, "GlobalChatPage")}
      {renderProtectedRoute("/audio-sentiment", <AudioSentiment />, "AudioSentiment")}
      {renderProtectedRoute("/esatsurvey", <ESATSurvey />, "ESATSurvey")}
      {renderProtectedRoute("/learning", <LearningPage />, "LearningPage")}

      {/* Default redirect to HRPay */}
      <Route path="/" element={<Navigate to="/hrpay" replace />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <Layout>
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">404</h1>
                <p className="text-lg text-gray-600 dark:text-gray-300">Page Not Found</p>
              </div>
            </div>
          </Layout>
        }
      />
    </Routes>
  </>
);

function App() {
  useEffect(() => {
    return () => console.log("🚀 App component unmounting");
  }, []);

  return (
    <Router>
      <DarkModeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <GnosisProvider>
            <AppRoutes />
          </GnosisProvider>
        </AuthProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
